import { useEffect } from 'react';
import { useReactFlow } from '@xyflow/react';
import { SweepCanvasRfNode } from '../../canvas-types/nodeTypesData';
import { useGetHeightFromContentObserver } from './useGetHeightFromContentObserver';

export interface UseNodeHeightFromContentBehaviorParams {
  ratio: number;
  height: number;
  nodeId: string;
  editMode: boolean;
  minHeight: number;
  defaultHeight: number;
}

/**
 * Observes content height and automatically adjusts node height based on content size.
 * Only adjusts height when in edit mode.
 * Returns a ref that should be attached to the content container element.
 */
export const useNodeHeightFromContentBehavior = ({
  ratio,
  height,
  nodeId,
  editMode,
  minHeight,
  defaultHeight,
}: UseNodeHeightFromContentBehaviorParams) => {
  const { updateNode } = useReactFlow<SweepCanvasRfNode>();
  const { observedHeight: observedHeight, boxRef } = useGetHeightFromContentObserver(ratio);

  useEffect(() => {
    // Only adjust height when in edit mode
    if (!editMode) return;

    const currentHeight = height || defaultHeight;

    // If observed height is bigger than current height, increase to observed height
    if (observedHeight > currentHeight) {
      updateNode(
        nodeId,
        (node: any) => ({
          ...node,
          height: observedHeight,
          data: {
            ...node.data,
            preventCanvasEvent: true,
          },
        }),
        { replace: false },
      );
    }
    // If observed height is less than current height and not less than min height, decrease to observed height
    else if (observedHeight < currentHeight && observedHeight >= minHeight) {
      updateNode(
        nodeId,
        { height: observedHeight < minHeight ? minHeight : observedHeight },
        { replace: false },
      );
    }
  }, [defaultHeight, editMode, height, minHeight, nodeId, observedHeight, updateNode]);

  return {
    boxRef,
  };
};
